import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { electronApis } from '@/features/electron-api';
import { logError } from '@/lib/errorLogging';

import type { MeetingTranscriptionInputDevicesPayload } from '@electron/shared/meetings.types';

type Payload = MeetingTranscriptionInputDevicesPayload;

/** The device list one consumer sees, with the one request in flight for it */
export class DeviceList {
  state: Payload | null = null;
  private inflight: Promise<Payload | null> | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly owner: DeviceLists) {}

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  read = () => this.state;

  set(next: Payload) {
    if (this.owner.disposed) return;
    this.state = next;
    for (const listener of this.listeners) listener();
  }

  /** Asks native once, however many callers ask while the answer is pending */
  refresh(): Promise<Payload | null> {
    if (this.inflight) return this.inflight;
    const request = electronApis.transcriptionInputDevice
      .getDevices()
      .then((next) => {
        this.set(next);
        return next;
      })
      .catch((error) => {
        logError(error, 'Failed to fetch transcription input devices');
        return null;
      })
      .finally(() => {
        if (this.inflight === request) this.inflight = null;
      });
    this.inflight = request;
    return request;
  }
}

/**
 * Every consumer's device list, owned by the window that renders them. Keyed
 * per consumer so hydration in one surface does not stall another. Disposed
 * with the window, after which late answers are dropped.
 */
export class DeviceLists {
  private readonly lists = new Map<string, DeviceList>();
  disposed = false;

  get(key: string): DeviceList {
    let list = this.lists.get(key);
    if (!list) {
      list = new DeviceList(this);
      this.lists.set(key, list);
    }
    return list;
  }

  /** Accepts answers again; React replays an effect's cleanup and setup in development */
  open() {
    this.disposed = false;
  }

  /**
   * Drops answers from now on. The lists themselves stay, because hooks
   * rendered before a StrictMode replay hold them and subscribe to them; a
   * replacement would show an empty list and ask native again.
   */
  dispose() {
    this.disposed = true;
  }
}

const DeviceListsContext = createContext<DeviceLists | null>(null);

/** Owns a window's device lists for as long as the window renders */
export function DeviceListsProvider({ children }: { children: ReactNode }) {
  const [lists] = useState(() => new DeviceLists());
  useEffect(() => {
    lists.open();
    return () => lists.dispose();
  }, [lists]);
  return (
    <DeviceListsContext.Provider value={lists}>
      {children}
    </DeviceListsContext.Provider>
  );
}

export function useDeviceLists(): DeviceLists {
  const lists = useContext(DeviceListsContext);
  if (!lists) {
    throw new Error(
      'useDeviceLists needs a DeviceListsProvider above it; every window renders one'
    );
  }
  return lists;
}
